/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { Hub } from 'interfaces/HubPage';
import type { ArticlePreview, RecentArticles, PinnedArticle } from 'interfaces/content/articles/Articles';
import PageHeader from 'components/PageHeader';
import { Microsites, SectionTitles, SectionTypes } from 'constants/constants';
import type { SectionsConfig } from 'api/MicrositeConfig/getMicrositeConfig';
import { ParseReadOurReview } from 'utils/miscUtils/rawHtml';
import type { EduRankings } from 'interfaces/EduRanking';
import { SectionVariant } from 'interfaces/Section';
import ScrollUpButton from 'components/Globals/Base/ScrollUpButton';
import type { PagePagination } from 'utils/miscUtils/calculatePageInfo';
import type { ProductTableData } from 'api/WidgetComponent/getProductTableWidgetData';
import SECTION_PAGE_TEST_IDS from 'constants/testsIds/sectionPage';
import { UIFragments } from 'utils/log/constants/uiFragments';
import ErrorBoundary from 'components/ErrorBoundary';
import dynamic from 'next/dynamic';
import * as S from './SectionPageStyles';

const WordPressWidget = dynamic(() => import('components/WordPressWidget'));
const PinnedArticlesList = dynamic(() => import('components/PinnedCards'));
const SubsectionCardList = dynamic(() => import('components/PinnedSubsectionCards'));
const RankingsSection = dynamic(() => import('components/education/EduRanking/RankingsSection'));
const ArticleSection = dynamic(() => import('components/recommends/ArticleSection'));
const ProductTable = dynamic(() => import('components/ProductTable'));
const TopArticles = dynamic(() => import('components/recommends/ArticleSection/TopArticles'));
const LatestArticles = dynamic(() => import('components/recommends/ArticleSection/LatestArticles'));

export interface SectionProps {
  section: Hub;
  platform: string;
  latestArticles: RecentArticles;
  latestArticlesHeroPosts?: ArticlePreview[];
  pinnedArticles: PinnedArticle[];
  mostPopular?: ArticlePreview[] | null;
  sectionsConfig?: SectionsConfig;
  subsectionsLatest?: Hub[];
  pagination?: PagePagination;
  widgets?: { [key: string]: string };
  eduRankings?: EduRankings;
  productTable?: ProductTableData;
}

const SectionPage = ({
  section,
  platform,
  pinnedArticles,
  latestArticles,
  latestArticlesHeroPosts,
  sectionsConfig,
  widgets,
  subsectionsLatest,
  pagination,
  eduRankings,
  productTable,
  mostPopular,
}: SectionProps) => {
  const isSection = section.sectionType === SectionTypes.SECTION;
  const isEducationSection = isSection && platform === Microsites.Education;
  const latestArticlesTopSection = isSection ? latestArticlesHeroPosts : null;
  const isFirstPage = pagination?.currentPage === 1;
  const readOurReviewsWidget = section.sectionType === SectionTypes.SUBSECTION && widgets?.['read-our-reviews-widget'];
  const topHighlightCardWidget = widgets?.['top-highlight-card-widget'];
  const contentHighlightCardWidget = widgets?.['content-highlight-card-widget'];

  function setTitle(): string {
    if (section.slug === 'articles') {
      return SectionTitles.articles;
    }
    if (isSection && !isEducationSection) {
      return SectionTitles.moreArticles;
    }
    return SectionTitles.latestArticles;
  }

  return (
    <S.Wrapper>
      {topHighlightCardWidget && (
        <ErrorBoundary fragment={UIFragments.TOP_HIGHLIGHT_CARD_WIDGET}>
          <WordPressWidget
            className='top-highlight-card'
            content={topHighlightCardWidget}
          />
        </ErrorBoundary>
      )}
      <S.ContentWrapper>
        <PageHeader
          className='page-header'
          title={section.title}
          pageInfo={pagination?.pageInfo}
          description={section.description}
          breadcrumbs={section.breadcrumbs}
          isUsingPaths
        >
          {!isSection && pinnedArticles && (
            <PinnedArticlesList
              className='pinned-articles'
              articles={pinnedArticles}
            />
          )}
          {isSection && section?.children && section.children.length > 0 && (
            <SubsectionCardList
              subsections={section.children.map((child) => ({
                icon: sectionsConfig?.[child.slug]?.icon,
                id: child.link,
                label: child.name,
                url: child.link,
              }))}
            />
          )}
        </PageHeader>
        {isFirstPage && (
          <>
            {contentHighlightCardWidget && (
              <ErrorBoundary fragment={UIFragments.CONTENT_HIGHLIGHT_CARD_WIDGET}>
                <WordPressWidget
                  className='content-highlight-card'
                  content={contentHighlightCardWidget}
                />
              </ErrorBoundary>
            )}
            {eduRankings && (
              <RankingsSection
                sectionTitle='Rankings'
                variant='education-section-rankings'
                {...eduRankings}
              />
            )}
            {isSection && !isEducationSection && latestArticlesTopSection && (
              <ArticleSection
                title={SectionTitles.latestArticles}
                variant={SectionVariant.SectionLatestArticles}
                articles={latestArticlesTopSection}
                trackingData={{
                  articleNameCD: section.title,
                  contentPlacementCD: `${section.name} grid`,
                }}
                dataCy={SECTION_PAGE_TEST_IDS.LATEST_ARTICLES}
              />
            )}
            {isSection &&
              subsectionsLatest &&
              subsectionsLatest.length > 0 &&
              subsectionsLatest.map((subsection) =>
                subsection.posts && subsection.posts.length > 0 ? (
                  <ArticleSection
                    key={subsection.slug}
                    title={subsection.title}
                    titleLink={subsection.link}
                    variant={SectionVariant.SubSection}
                    pinnedLinks={(subsection.children || []).map((topic) => ({
                      key: topic.slug,
                      title: topic.name,
                      url: topic.link,
                    }))}
                    articles={subsection.posts}
                    trackingData={{
                      articleNameCD: section.title,
                      contentPlacementCD: `${subsection.title} grid`,
                    }}
                    dataCy={`${SECTION_PAGE_TEST_IDS.SUBSECTION}-${subsection.slug}`}
                  />
                ) : null,
              )}
            {productTable && <ProductTable productTableData={productTable} />}
            {readOurReviewsWidget && (
              <ErrorBoundary fragment={UIFragments.READ_OUR_REVIEWS_WIDGET}>
                {ParseReadOurReview(readOurReviewsWidget)}
              </ErrorBoundary>
            )}
            {mostPopular && mostPopular.length > 0 && (
              <TopArticles
                title={SectionTitles.topArticles}
                articles={mostPopular}
                trackingData={{
                  articleNameCD: section.title,
                  contentPlacementCD: `${section.name} grid - row`,
                }}
              />
            )}
          </>
        )}
        <ErrorBoundary
          fragment={UIFragments.LATEST_ARTICLES}
          disabled={!isFirstPage}
        >
          {latestArticles.posts && latestArticles.posts.length > 0 && (
            <LatestArticles
              pagination={pagination!}
              title={setTitle()}
              sectionTitle={section.title}
              variant={isSection && !isEducationSection ? SectionVariant.MoreArticles : SectionVariant.LatestArticles}
              articles={latestArticles.posts}
              platform={platform}
              pinnedLinks={!isSection ? section.children : null}
              trackingData={{
                articleNameCD: section.title,
                contentPlacementCD: `${section.name} grid - row`,
              }}
              sectionType={section.sectionType}
            />
          )}
        </ErrorBoundary>
        <ScrollUpButton isRecommends />
      </S.ContentWrapper>
    </S.Wrapper>
  );
};

export default SectionPage;
